import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { ACADEMIC_ROLES } from '../../../data/academicTaxonomy';
import { VELLUM as T, SANS, mono } from '../folio/kit';

/* Choosing a title, in the folio vocabulary.

   A combobox is a field with a schedule of permitted values hanging off it,
   so it is drawn as one: a square cell, an ink rule, and a list ruled like
   any other schedule on the sheet. It carried the old palette's rounded
   box, grey fill and blue focus ring long after the rest of the site had
   moved, which made it the one foreign object on an otherwise ruled form.

   `bare` drops the control's own box for callers that already draw a cell
   around it - a title-block FieldRow, mainly. Standalone callers keep the
   box, because a field with no boundary is not a field. */

const CP = 'jtc';

interface JobTitleComboboxProps {
  value: string;
  onChange: (next: string) => void;
  options?: ReadonlyArray<string>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /* Drop the control's own border and ground; the caller draws the cell. */
  bare?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

const JobTitleCombobox: React.FC<JobTitleComboboxProps> = ({
  value,
  onChange,
  options = ACADEMIC_ROLES,
  placeholder = 'Search or type a job title…',
  disabled = false,
  required = false,
  bare = false,
  id,
  name,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return [...options].sort((a, b) => a.localeCompare(b));
    return [...options]
      .filter((r) => r.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.toLowerCase().startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return a.localeCompare(b);
      });
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectOption = (option: string) => {
    onChange(option);
    setOpen(false);
    inputRef.current?.blur();
  };

  const moveActive = (direction: 1 | -1) => {
    if (filtered.length === 0) return;
    let next = activeIndex + direction;
    if (next < 0) next = filtered.length - 1;
    if (next >= filtered.length) next = 0;
    setActiveIndex(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0) {
        e.preventDefault();
        const option = filtered[activeIndex];
        if (option) selectOption(option);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)} style={{ position: 'relative' }}>
      <div
        className={`${CP}-box${bare ? '' : ` ${CP}-boxed`}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          width: '100%',
          border: bare ? 'none' : `1.5px solid ${T.ink}`,
          background: bare ? 'transparent' : T.ground,
          padding: bare ? 0 : '2px 12px',
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="11" cy="11" r="6.5" stroke={T.mute} strokeWidth="1.6" />
          <path d="m20 20-4-4" stroke={T.mute} strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id || 'job-title'}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          required={required}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            if (!open) setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={handleKeyDown}
          className={`${CP}-input`}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: bare ? '9px 0' : '11px 0',
            fontFamily: SANS,
            fontSize: 15.5,
            color: T.ink,
          }}
        />
        <svg
          aria-hidden
          width="9"
          height="6"
          viewBox="0 0 9 6"
          fill="none"
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms ease',
          }}
        >
          <path d="M1 1L4.5 4.5L8 1" stroke={T.mute} strokeWidth="1.3" strokeLinecap="square" />
        </svg>
      </div>

      {open && (
        <ul
          ref={listRef}
          id={`${id || 'job-title'}-listbox`}
          role="listbox"
          className={`${CP}-list`}
          style={{
            position: 'absolute',
            zIndex: 30,
            top: '100%',
            left: 0,
            marginTop: 4,
            width: '100%',
            maxHeight: 288,
            overflowY: 'auto',
            listStyle: 'none',
            padding: 0,
            border: `1.5px solid ${T.ink}`,
            background: T.ground,
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ ...mono(T), padding: '14px 13px', letterSpacing: '0.1em' }}>
              No match — your title will be used as typed.
            </li>
          ) : (
            filtered.map((option, index) => {
              const isActive = index === activeIndex;
              const isSelected = option === value;
              return (
                <li
                  key={option}
                  role="option"
                  aria-selected={isSelected}
                  data-row-index={index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(option);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                    padding: '9px 13px',
                    fontFamily: SANS,
                    fontSize: 14.5,
                    color: T.ink,
                    fontWeight: isSelected ? 600 : 400,
                    background: isActive ? 'rgba(10,110,126,0.08)' : 'transparent',
                    borderTop: index === 0 ? 'none' : `1px solid ${T.rule}`,
                  }}
                >
                  {/* The entry in hand is marked on the rule, not by a fill
                      alone - a tint is not visible to everyone. */}
                  <span
                    aria-hidden
                    style={{
                      width: 2,
                      alignSelf: 'stretch',
                      background: isActive || isSelected ? T.accent : 'transparent',
                      flexShrink: 0,
                    }}
                  />
                  {option}
                </li>
              );
            })
          )}
        </ul>
      )}

      <style>{`
        .${CP}-input::placeholder { color: ${T.mute}; opacity: 1; }
        /* Only the control that draws its own box takes the focus ring.
           Bare, the cell around it has already taken it, and two rings on
           one field read as two fields. */
        .${CP}-boxed:focus-within { box-shadow: inset 0 0 0 1.5px ${T.accent}; }
      `}</style>
    </div>
  );
};

export default JobTitleCombobox;
